import React, { useState, useRef, useEffect, useCallback, useImperativeHandle } from 'react';
import { ChevronDown } from 'lucide-react';

// Walk children (including fragments) and collect <option> descriptors so the
// existing call sites — which pass native <option> elements — keep working
// without modification.
const extractOptions = (children) => {
  const result = [];
  const walk = (nodes) => {
    React.Children.forEach(nodes, (child) => {
      if (!React.isValidElement(child)) return;
      if (child.type === React.Fragment) {
        walk(child.props.children);
        return;
      }
      if (child.type === 'option') {
        const rawValue = child.props.value;
        const labelNode = child.props.children;
        let label;
        if (typeof labelNode === 'string' || typeof labelNode === 'number') {
          label = String(labelNode);
        } else {
          label = React.Children.toArray(labelNode).join('');
        }
        // Match native <select>: when no value attr is provided, fall back to
        // the option's label text instead of coercing to ''. Otherwise every
        // bare <option>Foo</option> in a list collapses to the same value.
        const hasExplicitValue = rawValue !== undefined && rawValue !== null;
        const value = hasExplicitValue ? rawValue : label;
        result.push({
          value: String(value),
          rawValue: value,
          label,
          disabled: !!child.props.disabled,
        });
      }
    });
  };
  walk(children);
  return result;
};

const Dropdown = React.forwardRef(({
  value,
  onChange,
  children,
  className = '',
  disabled = false,
  name,
  id,
  placeholder,
  onClick,
  onKeyDown,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
  ...rest
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);

  useImperativeHandle(ref, () => buttonRef.current);

  const options = extractOptions(children);
  const normalizedValue = value === undefined || value === null ? '' : String(value);
  const selected = options.find(opt => opt.value === normalizedValue);

  // If no selected entry, fall back to the first option when it's acting as a
  // placeholder (value === ''), else show the explicit placeholder prop.
  let displayLabel = '';
  let isPlaceholder = false;
  if (selected) {
    displayLabel = selected.label;
    isPlaceholder = selected.value === '';
  } else if (placeholder) {
    displayLabel = placeholder;
    isPlaceholder = true;
  } else if (options.length > 0 && options[0].value === '') {
    displayLabel = options[0].label;
    isPlaceholder = true;
  }

  const fireChange = useCallback((nextRawValue) => {
    if (!onChange) return;
    const nextValue = nextRawValue === undefined || nextRawValue === null ? '' : nextRawValue;
    // Match the native <select> event shape so existing callers
    // `(e) => ...e.target.value` and RHF's field.onChange keep working.
    const syntheticEvent = {
      target: { value: nextValue, name },
      currentTarget: { value: nextValue, name },
    };
    onChange(syntheticEvent);
  }, [onChange, name]);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setHighlightedIndex(-1);
  }, []);

  const openMenu = useCallback(() => {
    if (disabled) return;
    setIsOpen(true);
    const idx = options.findIndex(opt => opt.value === normalizedValue && !opt.disabled);
    const fallback = options.findIndex(opt => !opt.disabled);
    setHighlightedIndex(idx >= 0 ? idx : fallback);
  }, [disabled, options, normalizedValue]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        closeMenu();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, closeMenu]);

  useEffect(() => {
    if (isOpen && highlightedIndex >= 0 && listRef.current) {
      const el = listRef.current.children[highlightedIndex];
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex, isOpen]);

  const selectOption = (opt) => {
    if (opt.disabled) return;
    fireChange(opt.rawValue);
    closeMenu();
    // Return focus to the trigger so keyboard users stay in place.
    if (buttonRef.current) buttonRef.current.focus();
  };

  const moveHighlight = (delta) => {
    if (!options.length) return;
    let next = highlightedIndex;
    for (let i = 0; i < options.length; i += 1) {
      next = (next + delta + options.length) % options.length;
      if (!options[next].disabled) {
        setHighlightedIndex(next);
        return;
      }
    }
  };

  const handleKeyDown = (event) => {
    if (disabled) return;
    if (!isOpen) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();
        openMenu();
      }
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        moveHighlight(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveHighlight(-1);
        break;
      case 'Home':
        event.preventDefault();
        setHighlightedIndex(options.findIndex(opt => !opt.disabled));
        break;
      case 'End':
        event.preventDefault();
        for (let i = options.length - 1; i >= 0; i -= 1) {
          if (!options[i].disabled) { setHighlightedIndex(i); break; }
        }
        break;
      case 'Enter':
        event.preventDefault();
        if (highlightedIndex >= 0 && options[highlightedIndex]) {
          selectOption(options[highlightedIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        closeMenu();
        break;
      case 'Tab':
        closeMenu();
        break;
      default:
        break;
    }
  };

  const handleToggle = () => {
    if (disabled) return;
    if (isOpen) closeMenu();
    else openMenu();
  };

  // Call sites migrated from <select> may pass their own onClick/onKeyDown (the
  // employee detail page lazy-loads banks from onClick). Run theirs, then ours:
  // if these rode along in {...rest} they would replace the toggle and the list
  // could never be opened with the mouse.
  const handleClick = (event) => {
    if (onClick) onClick(event);
    handleToggle();
  };

  const handleKeyDownWithConsumer = (event) => {
    if (onKeyDown) onKeyDown(event);
    handleKeyDown(event);
  };

  const baseClasses = 'relative w-full border border-gray-300 rounded-md pl-3 pr-9 py-2 bg-white text-sm text-left focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500';
  const stateClasses = disabled ? 'bg-gray-100 cursor-not-allowed text-gray-500' : 'cursor-pointer';
  const combinedClasses = `${baseClasses} ${stateClasses} ${className}`.trim();

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        ref={buttonRef}
        id={id}
        name={name}
        disabled={disabled}
        onClick={handleClick}
        onKeyDown={handleKeyDownWithConsumer}
        className={combinedClasses}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        {...(dataTestId ? { 'data-testid': dataTestId } : {})}
        {...rest}
      >
        <span className={`block truncate ${isPlaceholder ? 'text-gray-500' : 'text-gray-900'}`}>
          {displayLabel || '\u00A0'}
        </span>
        <ChevronDown
          className={`pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <ul
          ref={listRef}
          className="absolute z-50 w-full mt-1 max-h-60 overflow-auto bg-white border border-gray-200 rounded-md shadow-lg py-1 focus:outline-none"
          role="listbox"
        >
          {options.length === 0 ? (
            <li className="px-3 py-2 text-sm text-gray-500">No options</li>
          ) : (
            options.map((opt, index) => {
              const isSelected = opt.value === normalizedValue;
              const isHighlighted = index === highlightedIndex;
              let itemClass;
              if (opt.disabled) {
                itemClass = 'text-gray-300 cursor-not-allowed';
              } else if (isSelected) {
                itemClass = 'bg-primary-50 text-primary-900 font-medium';
              } else if (isHighlighted) {
                itemClass = 'bg-gray-100 text-gray-900';
              } else {
                itemClass = 'text-gray-900 hover:bg-gray-50';
              }
              return (
                <li
                  key={`${opt.value}-${index}`}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={opt.disabled || undefined}
                  className={`px-3 py-2 text-sm select-none ${itemClass}`}
                  {...(dataTestId
                    ? { 'data-testid': `${dataTestId}-option-${opt.value}` }
                    : {})}
                  onMouseDown={(event) => {
                    // mousedown + preventDefault keeps the trigger focused so
                    // the focus ring behaves correctly.
                    event.preventDefault();
                    selectOption(opt);
                  }}
                  onMouseEnter={() => { if (!opt.disabled) setHighlightedIndex(index); }}
                >
                  {opt.label || '\u00A0'}
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
});

Dropdown.displayName = 'Dropdown';

export default Dropdown;
